import { OdooHistoryItem } from "../../types/odoo/odooHistory";
import { WorkorderStatus } from "../../types/workorder/workorderStatus";

const RETRYABLE_ODOO_HISTORY_STATUSES = ["ERROR", "ERROR_RETRY"];
const ALLOWED_ODOO_STOCK_STATUSES = [
  "ERROR",
  "ERROR_RETRY",
  "ERROR_NO_RETRY",
];
const ODOO_STOCK_ACTIONS = ["DESTOCK", "STOCK"];
// Odoo sale.order states in which the document is no longer an editable quotation. Pushing a
// workorder rewrites the order lines of the existing sale.order in place, so it must never touch
// one the sales team has already confirmed, locked or cancelled. Odoo gives us the state as free
// text with no enum behind it, so anything unrecognised counts as still editable: an unknown
// state must not silently disable the button the way `!invoice` used to.
const CONFIRMED_ODOO_INVOICE_STATES = ["sale", "done", "cancel"];
// 5 minutes de peremption cote core, plus la minute du cron qui la constate.
const ODOO_ACTION_STALE_MS = 6 * 60 * 1000;

interface LatestOdooActionIds {
  send?: string;
  destock?: string;
}

export const hasWorkorderHoses = (workorder: EasyTech.Workorder): boolean =>
  (workorder.hoses?.length ?? 0) > 0 ||
  !!workorder.machines?.some((machine) => (machine.hoses?.length ?? 0) > 0);

export const isOdooStockAction = (action: string): boolean =>
  ODOO_STOCK_ACTIONS.includes(action);

export const isRetryableOdooHistoryStatus = (status: string): boolean =>
  RETRYABLE_ODOO_HISTORY_STATUSES.includes(status);

// A workorder already sent to Odoo stays sendable: re-sending updates the existing quotation
// rather than creating a second one. Only a confirmed sale order, or stock already moved out,
// makes the push unsafe.
export const isOdooQuotationEditable = (
  invoice?: EasyTech.WorkorderInvoice | null
): boolean =>
  !invoice ||
  !CONFIRMED_ODOO_INVOICE_STATES.includes(invoice.state?.toLowerCase() ?? "");

// L'envoi est synchrone cote core et deja protege par preSend, qui refuse un second SEND tant que
// le premier tourne. Mais `loading` ne connait que l'onglet courant : un envoi lance ailleurs
// (autre planificateur, autre onglet, page rechargee) laissait le bouton actif, et le clic
// revenait avec le toast d'erreur generique au lieu de dire qu'une synchro est en cours.
// L'historique est la seule trace d'un SEND en vol, ExternalActionService.init l'ecrivant
// en IN_PROGRESS.
export const isOdooSendInProgress = (
  history: OdooHistoryItem[] = [],
  now: number = Date.now()
): boolean => {
  const latestSend = [...history]
    .sort((a, b) => b.created - a.created)
    .find((item) => item.action === "SEND");

  if (latestSend?.status !== "IN_PROGRESS") {
    return false;
  }

  // ExternalActionTimeoutScheduler passe toutes les minutes et bascule en ERROR_RETRY toute action
  // IN_PROGRESS dont `updated` depasse 5 minutes : au-dela de 6 minutes le core considere donc la
  // ligne morte, et laisserait passer un nouvel envoi. S'aligner sur cette borne evite de griser
  // un bouton que le serveur accepterait de toute facon, et evite qu'un onglet ouvert avant la
  // synchro reste bloque sur un IN_PROGRESS que plus personne ne fera avancer.
  const lastTouched = latestSend.updated || latestSend.created;

  return now - lastTouched < ODOO_ACTION_STALE_MS;
};

export const canSendWorkorderToOdoo = ({
  workorder,
  loading = false,
}: {
  workorder: EasyTech.Workorder;
  loading?: boolean;
}): boolean =>
  !loading &&
  workorder.status === WorkorderStatus.VALIDATED &&
  isOdooQuotationEditable(workorder.invoice) &&
  workorder.invoice?.stock?.status !== "SUCCESS";

export const canDestockWorkorder = ({
  workorder,
  loading = false,
}: {
  workorder: EasyTech.Workorder;
  loading?: boolean;
}): boolean => {
  const stock = workorder.invoice?.stock;

  return (
    !loading &&
    workorder.status === WorkorderStatus.VALIDATED &&
    !!workorder.invoice &&
    hasWorkorderHoses(workorder) &&
    stock?.status !== "SUCCESS" &&
    stock?.status !== "PENDING" &&
    (!stock || ALLOWED_ODOO_STOCK_STATUSES.includes(stock.status))
  );
};

export interface OdooTransferEstimate {
  partsCount: number;
  estimatedSeconds: number;
}

export const splitOdooTransferDuration = (
  seconds: number
): { count: number; unit: "seconds" | "minutes" } => {
  if (seconds < 60) {
    return { count: Math.max(1, Math.round(seconds)), unit: "seconds" };
  }
  return { count: Math.max(1, Math.ceil(seconds / 60)), unit: "minutes" };
};

export const canRetryOdooHistoryItem = ({
  item,
  latestActions,
  workorder,
}: {
  item: OdooHistoryItem;
  latestActions: LatestOdooActionIds;
  workorder: EasyTech.Workorder;
}): boolean => {
  const isLatestAction =
    (item.action === "SEND" && item.id === latestActions.send) ||
    (isOdooStockAction(item.action) && item.id === latestActions.destock);

  return (
    isLatestAction &&
    isRetryableOdooHistoryStatus(item.status) &&
    workorder.status === WorkorderStatus.VALIDATED &&
    (item.action !== "SEND" || isOdooQuotationEditable(workorder.invoice))
  );
};
